import type {
  InsuranceProductPolicyItemConfiguration,
  InsuranceProductPolicyItemConfigurationResolver,
} from '../../../model/policy-item-configuration.ts'
import type { PolicyItems } from '../../../model/policy-item.ts'
import {
  type FrMrgaPolicyItem,
  type FrMrgaPolicyItemsState,
  isFrMrgaPolicyItemAvailable,
} from '../fr-mrga-insurance.ts'

import { frMrgaDeductibleByPolicyItem_v1_0_0 as deductibles } from './deductible-by-policy-item.ts'
import { frMrgaLimitOfIndemnityByPolicyItem_v1_0_0 as limits } from './limit-of-indemnity-by-policy-item.ts'
import { frMrgaScopeByPolicyItem_v1_0_0 as scopes } from './scope-by-policy-item.ts'

const unavailable: InsuranceProductPolicyItemConfiguration = {
  isHidden: true,
  canBeUpdated: false,
  canBeSelected: false,
}

const included: InsuranceProductPolicyItemConfiguration = {
  isHidden: false,
  canBeUpdated: false,
  canBeSelected: false,
}

function includedEditConfigurationOf(
  policyItemName: FrMrgaPolicyItem,
): InsuranceProductPolicyItemConfigurationResolver<FrMrgaPolicyItemsState> {
  const scope = scopes[policyItemName]
  return ({ state }) => (isFrMrgaPolicyItemAvailable(scope, state) ? included : unavailable)
}

const option = (): InsuranceProductPolicyItemConfiguration => ({
  isHidden: false,
  canBeUpdated: false,
  canBeSelected: true,
})

export const frMrgaPolicyItems_v1_0_0 = {
  publicLiability: {
    name: 'publicLiability',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.publicLiability.limit,
    possibleDeductibles: deductibles.publicLiability.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('publicLiability'),
    additionalInfo: {
      limitOfIndemnity: limits.publicLiability.additionalInfo,
      deductible: deductibles.publicLiability.additionalInfo,
    },
  },
  productsAndCompletedWorksLiability: {
    name: 'productsAndCompletedWorksLiability',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.productsAndCompletedWorksLiability.limit,
    possibleDeductibles: deductibles.productsAndCompletedWorksLiability.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('productsAndCompletedWorksLiability'),
    additionalInfo: {
      limitOfIndemnity: limits.productsAndCompletedWorksLiability.additionalInfo,
      deductible: deductibles.productsAndCompletedWorksLiability.additionalInfo,
    },
  },
  fire: {
    name: 'fire',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.fire.select,
    possibleDeductibles: deductibles.fire.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('fire'),
    additionalInfo: {
      limitOfIndemnity: limits.fire.additionalInfo,
      deductible: deductibles.fire.additionalInfo,
    },
  },
  electricalDamage: {
    name: 'electricalDamage',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.electricalDamage.select,
    possibleDeductibles: deductibles.electricalDamage.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('electricalDamage'),
    additionalInfo: {
      limitOfIndemnity: limits.electricalDamage.additionalInfo,
      deductible: deductibles.electricalDamage.additionalInfo,
    },
  },
  climateEvents: {
    name: 'climateEvents',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.climateEvents.select,
    possibleDeductibles: deductibles.climateEvents.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('climateEvents'),
    additionalInfo: {
      limitOfIndemnity: limits.climateEvents.additionalInfo,
      deductible: deductibles.climateEvents.additionalInfo,
    },
  },
  naturalDisasters: {
    name: 'naturalDisasters',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.naturalDisasters.select,
    possibleDeductibles: deductibles.naturalDisasters.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('naturalDisasters'),
    additionalInfo: {
      limitOfIndemnity: limits.naturalDisasters.additionalInfo,
      deductible: deductibles.naturalDisasters.additionalInfo,
    },
  },
  terrorism: {
    name: 'terrorism',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.terrorism.select,
    possibleDeductibles: deductibles.terrorism.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('terrorism'),
    additionalInfo: {
      limitOfIndemnity: limits.terrorism.additionalInfo,
      deductible: deductibles.terrorism.additionalInfo,
    },
  },
  waterDamage: {
    name: 'waterDamage',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.waterDamage.select,
    possibleDeductibles: deductibles.waterDamage.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('waterDamage'),
    additionalInfo: {
      limitOfIndemnity: limits.waterDamage.additionalInfo,
      deductible: deductibles.waterDamage.additionalInfo,
    },
  },
  buildingDamageAfterTheft: {
    name: 'buildingDamageAfterTheft',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.buildingDamageAfterTheft.select,
    possibleDeductibles: deductibles.buildingDamageAfterTheft.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('buildingDamageAfterTheft'),
    additionalInfo: {
      limitOfIndemnity: limits.buildingDamageAfterTheft.additionalInfo,
      deductible: deductibles.buildingDamageAfterTheft.additionalInfo,
    },
  },
  theft: {
    name: 'theft',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.theft.select,
    possibleDeductibles: deductibles.theft.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('theft'),
    additionalInfo: {
      limitOfIndemnity: limits.theft.additionalInfo,
      deductible: deductibles.theft.additionalInfo,
    },
  },
  glass: {
    name: 'glass',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.glass.select,
    possibleDeductibles: deductibles.glass.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('glass'),
    additionalInfo: {
      limitOfIndemnity: limits.glass.additionalInfo,
      deductible: deductibles.glass.additionalInfo,
    },
  },
  machineryAndComputerBreakage: {
    name: 'machineryAndComputerBreakage',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.machineryAndComputerBreakage.select,
    possibleDeductibles: deductibles.machineryAndComputerBreakage.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('machineryAndComputerBreakage'),
    additionalInfo: {
      limitOfIndemnity: limits.machineryAndComputerBreakage.additionalInfo,
      deductible: deductibles.machineryAndComputerBreakage.additionalInfo,
    },
  },
  businessInterruption: {
    name: 'businessInterruption',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.businessInterruption.limit,
    possibleDeductibles: deductibles.businessInterruption.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('businessInterruption'),
    additionalInfo: {
      limitOfIndemnity: limits.businessInterruption.additionalInfo,
      deductible: deductibles.businessInterruption.additionalInfo,
    },
  },
  civilLiability: {
    name: 'civilLiability',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.civilLiability.limit,
    possibleDeductibles: deductibles.civilLiability.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('civilLiability'),
    additionalInfo: {
      limitOfIndemnity: limits.civilLiability.additionalInfo,
      deductible: deductibles.civilLiability.additionalInfo,
    },
  },
  criminalDefenseAndRecourse: {
    name: 'criminalDefenseAndRecourse',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.criminalDefenseAndRecourse.limit,
    possibleDeductibles: deductibles.criminalDefenseAndRecourse.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('criminalDefenseAndRecourse'),
    additionalInfo: {
      limitOfIndemnity: limits.criminalDefenseAndRecourse.additionalInfo,
      deductible: deductibles.criminalDefenseAndRecourse.additionalInfo,
    },
  },
  driverProtection: {
    name: 'driverProtection',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.driverProtection.limit,
    possibleDeductibles: deductibles.driverProtection.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('driverProtection'),
    additionalInfo: {
      limitOfIndemnity: limits.driverProtection.additionalInfo,
      deductible: deductibles.driverProtection.additionalInfo,
    },
  },
  ownedVehiclesDamage: {
    name: 'ownedVehiclesDamage',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.ownedVehiclesDamage.limit,
    possibleDeductibles: deductibles.ownedVehiclesDamage.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('ownedVehiclesDamage'),
    additionalInfo: {
      limitOfIndemnity: limits.ownedVehiclesDamage.additionalInfo,
      deductible: deductibles.ownedVehiclesDamage.additionalInfo,
    },
  },
  personalBelongingsAndAccessories: {
    name: 'personalBelongingsAndAccessories',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.personalBelongingsAndAccessories.limit,
    possibleDeductibles: deductibles.personalBelongingsAndAccessories.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('personalBelongingsAndAccessories'),
    additionalInfo: {
      limitOfIndemnity: limits.personalBelongingsAndAccessories.additionalInfo,
      deductible: deductibles.personalBelongingsAndAccessories.additionalInfo,
    },
  },
  vehiclesForSaleDamage: {
    name: 'vehiclesForSaleDamage',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.vehiclesForSaleDamage.limit,
    possibleDeductibles: deductibles.vehiclesForSaleDamage.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('vehiclesForSaleDamage'),
    additionalInfo: {
      limitOfIndemnity: limits.vehiclesForSaleDamage.additionalInfo,
      deductible: deductibles.vehiclesForSaleDamage.additionalInfo,
    },
  },
  entrustedVehiclesDamage: {
    name: 'entrustedVehiclesDamage',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.entrustedVehiclesDamage.select,
    possibleDeductibles: deductibles.entrustedVehiclesDamage.deductible,
    dependsOn: [],
    editConfiguration: includedEditConfigurationOf('entrustedVehiclesDamage'),
    additionalInfo: {
      limitOfIndemnity: limits.entrustedVehiclesDamage.additionalInfo,
      deductible: deductibles.entrustedVehiclesDamage.additionalInfo,
    },
  },
  legalProtection: {
    name: 'legalProtection',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: limits.legalProtection.limit,
    possibleDeductibles: deductibles.legalProtection.deductible,
    dependsOn: [],
    editConfiguration: option,
    additionalInfo: {
      limitOfIndemnity: limits.legalProtection.additionalInfo,
      deductible: deductibles.legalProtection.additionalInfo,
    },
  },
  assistance: {
    name: 'assistance',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: limits.assistance.limit,
    possibleDeductibles: deductibles.assistance.deductible,
    dependsOn: [],
    editConfiguration: option,
    additionalInfo: {
      limitOfIndemnity: limits.assistance.additionalInfo,
      deductible: deductibles.assistance.additionalInfo,
    },
  },
} as const satisfies PolicyItems<FrMrgaPolicyItem, FrMrgaPolicyItemsState>
